import api from "./api";
import { resolveViewRoute } from "./publicViewApi";

class SynchronizationService {
  // Buscar todas as sincronizações com paginação
  static async getSynchronizations(filters = {}) {
    try {
      const requestData = {
        page: filters.page || 1,
        pageSize: filters.pageSize || 5,
        nomeERP: filters.nomeERP || "",
        projectId: filters.projectId || null,
        erp: filters.erp >= 0 ? filters.erp : null,
        status: filters.status || null,
        sorterField: filters.sorterField || "",
        sortOrder: filters.sortOrder || "desc",
      };

      const response = await api.post("/sync/getAll", requestData);
      return response.data?.response || [];
    } catch (error) {
      console.error("Erro ao buscar sincronizações:", error);
      throw error;
    }
  }

  static async getStats(startDate, endDate) {
    try {
      const payload = {
        startDate: startDate,
        endDate: endDate,
      };
      const response = await api.post("/sync/getStats", payload);
      return response;
    } catch (error) {
      console.error("❌ Erro ao buscar stats de tenants:", error);
      throw error;
    }
  }
  static async downloadModeloPadrao() {
    try {
      const response = await api.get("/sync/downloadModeloPadrao", {
        responseType: "blob",
      });
      return response;
    } catch (error) {
      console.error("Erro ao baixar modelo padrão:", error);
      throw error;
    }
  }

  static async getSynchronizationsSimplified(filters = {}) {
    try {
      const requestData = {
        page: filters.page || 1,
        pageSize: filters.pageSize || 5,
        nomeERP: filters.nomeERP || "",
        projectId: filters.projectId || null,
        erp: filters.erp >= 0 ? filters.erp : null,
        status: filters.status || null,
        sorterField: filters.sorterField || "",
        sortOrder: filters.sortOrder || "desc",
        ...(typeof filters.simpleData === "boolean"
          ? { simpleData: filters.simpleData }
          : {}),
      };

      const response = await api.post("/sync/getAllSimplified", requestData);
      return response.data?.response || [];
    } catch (error) {
      console.error("Erro ao buscar sincronizações:", error);
      throw error;
    }
  }

  // Buscar sincronização por ID
  static async getSynchronizationById(id) {
    try {
      const { client, url } = resolveViewRoute(
        `/sync/getById/${id}`,
        `/public/view/sync/${id}`,
      );
      const response = await client.get(url);
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar sincronização:", error);
      throw error;
    }
  }

  /**
   * Cota de empresas do plano do parceiro: { enforced, planoNome, cap, total,
   * disponivel, podeAdicionar, mensagem }. Usada para avisar em tela ANTES de abrir
   * o cadastro — o bloqueio efetivo é do backend, no /sync/upsert.
   * Falha de rede não pode impedir o fluxo: devolve cota "sem limite" (fail-open).
   */
  static async getCompanyQuota() {
    try {
      const response = await api.get("/sync/companyQuota");
      return response.data?.response ?? null;
    } catch (error) {
      console.error("Erro ao buscar a cota de empresas do plano:", error);
      return null;
    }
  }
  
  /** 
   * Contexto customizado da empresa para o Fechamento (insumo do prompt de IA).
   * Mesmo par de endpoints usado pela lista de Sincronizações e pela lista de empresas de Planos.
   */
  static async getClosingContext(syncId) {
    const response = await api.get(`/sync/${syncId}/closing-context`);
    return response.data?.response ?? null;
  }

  /** Grava o contexto; `null`/vazio remove. */
  static async updateClosingContext(syncId, closingContext) {
    const response = await api.patch(`/sync/${syncId}/closing-context`, {
      closingContext: closingContext ? closingContext : null,
    });
    return response.data?.response ?? null;
  }

  /**
   * Filtros de contexto do Fechamento (Categoria/Departamento/Conta/Projeto/Pessoa), todos
   * "somente Ignorar" (exclusão), aplicados a TODAS as seções. Mesmo par de endpoints (GET
   * carrega, PATCH grava) do Contexto do Fechamento.
   */
  static async getClosingContextFilters(syncId) {
    const response = await api.get(`/sync/${syncId}/closing-context-filters`);
    return response.data?.response ?? null;
  }

  /** Grava os 5 filtros; cada um uma lista de ids (array vazio = sem filtro naquela dimensão). */
  static async updateClosingContextFilters(syncId, filters) {
    const response = await api.patch(`/sync/${syncId}/closing-context-filters`, {
      categoryIds: Array.isArray(filters?.categoryIds) ? filters.categoryIds : [],
      departmentIds: Array.isArray(filters?.departmentIds) ? filters.departmentIds : [],
      accountIds: Array.isArray(filters?.accountIds) ? filters.accountIds : [],
      projectIds: Array.isArray(filters?.projectIds) ? filters.projectIds : [],
      personIds: Array.isArray(filters?.personIds) ? filters.personIds : [],
    });
    return response.data?.response ?? null;
  }

  /** Conta Azul v2: persistir se o DRE segue a configuração do ERP. */
  static async setSyncUseErpDreConfig(syncId, useErpDreConfig) {
    try {
      const response = await api.post("/sync/setUseErpDreConfig", {
        SyncId: syncId,
        UseErpDreConfig: !!useErpDreConfig,
      });
      return response.data;
    } catch (error) {
      console.error("Erro ao atualizar opção de DRE do ERP:", error);
      throw error;
    }
  }

  /**
   * Habilita o DRE padrão do ERP, apagando configs locais
   * (SyncDRE/SyncDRECat/SyncKPIDRE) e solicitando atualização ao integrador.
   */
  static async enableErpDreConfig(syncId) {
    try {
      const response = await api.post("/sync/enableUseErpDreConfig", {
        SyncId: syncId,
      });
      return response.data;
    } catch (error) {
      console.error("Erro ao habilitar DRE padrão do ERP:", error);
      throw error;
    }
  }

  static async enableContaAzulV2ErpDreConfig(syncId) {
    return this.enableErpDreConfig(syncId);
  }

  /**
   * Conta Azul v2: busca o de-para de operações financeiras padrão (Fretes, Multas, Juros, etc.).
   * @param {string} syncId
   * @returns {Promise<Array<{id:string, syncId:string, operation:number, codCategoria:string}>>}
   */
  static async getDefaultFinancialCategories(syncId) {
    try {
      const response = await api.get(
        `/sync/getDefaultFinancialCategories/${syncId}`,
      );
      return response.data?.response ?? [];
    } catch (error) {
      console.error(
        "Erro ao buscar categorias financeiras padrão:",
        error,
      );
      throw error;
    }
  }

  /**
   * Conta Azul v2: salva o de-para de operações financeiras padrão.
   * Items: [{ operation: number, codCategoria: string }]. Operações sem categoria são removidas.
   */
  static async upsertDefaultFinancialCategories(syncId, items) {
    try {
      const payload = {
        SyncId: syncId,
        Items: (items || []).map((it) => ({
          Id: it.id ?? "00000000-0000-0000-0000-000000000000",
          SyncId: syncId,
          Operation: it.operation,
          CodCategoria: it.codCategoria ?? "",
          IsDeleted: !!it.isDeleted,
        })),
      };
      const response = await api.post(
        "/sync/upsertDefaultFinancialCategories",
        payload,
      );
      return response.data?.response ?? [];
    } catch (error) {
      console.error(
        "Erro ao salvar categorias financeiras padrão:",
        error,
      );
      throw error;
    }
  }

  /**
   * Busca os syncs dependentes (ex.: empresas consolidadas) de um sync por ID.
   * @param {string} syncId - ID do sync (GUID)
   * @returns {Promise<Array<{ value: string, label: string }>>} Lista de { value, label }
   */
  static async getSyncsDependentes(syncId) {
    try {
      const { client, url } = resolveViewRoute(
        `/sync/getSyncsDependentes/${syncId}`,
        `/public/view/sync/${syncId}/dependentes`,
      );
      const response = await client.post(url, {});
      return response.data?.response ?? [];
    } catch (error) {
      console.error("Erro ao buscar syncs dependentes:", error);
      throw error;
    }
  }

  static async getSynchronizationKPIDRE(id) {
    try {
      const response = await api.get(`/sync/getSyncKPIDRE/${id}`);
      return response.data?.response ?? response.data ?? [];
    } catch (error) {
      console.error("Erro ao buscar KPIs de DRE:", error);
      throw error;
    }
  }

  static async upsertSyncKPIDRE(syncKpiDreArray) {
    try {
      if (!Array.isArray(syncKpiDreArray)) {
        throw new Error("O payload de upsertSyncKPIDRE deve ser um array");
      }
      // O backend espera um JSON array (List<SyncKPIDREDto>) no corpo
      const response = await api.post(
        "/sync/upsertSyncKPIDRE",
        syncKpiDreArray,
      );
      return response.data;
    } catch (error) {
      console.error("Erro ao salvar KPIs de DRE:", error);
    }
  }
  // Buscar sincronização por ID
  static async getSynchronizationByIdHistory(id) {
    try {
      const response = await api.get(`/sync/getByIdSyncHistory/${id}`);
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar sincronização:", error);
      throw error;
    }
  }

  // Criar ou atualizar sincronização (Upsert)
  static async upsertSynchronization(synchronizationData) {
    try {
      // Inclui parâmetros de configuração se existirem
      const payload = { ...synchronizationData };

      // Mapear parâmetros para os nomes corretos do backend
      if (synchronizationData.dfcConfig) {
        payload.SyncDFCs = synchronizationData.dfcConfig;
      }
      if (synchronizationData.financialGroupConfig) {
        payload.SyncFinancialGroups = synchronizationData.financialGroupConfig;
      }
      if (synchronizationData.kpiConfig) {
        payload.SyncKPIs = synchronizationData.kpiConfig;
      }
      if (synchronizationData.DreConfig) {
        payload.SyncDREs = synchronizationData.DreConfig;
      }

      // Garantir que o ID seja enviado corretamente
      if (synchronizationData.Id) {
        payload.Id = synchronizationData.Id;
      } else if (synchronizationData.id) {
        payload.Id = synchronizationData.id;
      }

      // Log detalhado para debug

      const response = await api.post("/sync/upsert", payload);

      return response.data;
    } catch (error) {
      console.error("❌ Erro ao criar/atualizar sincronização:", error);
      console.error("❌ Detalhes do erro:", error.response?.data);
      throw error;
    }
  }

  // Excluir sincronização
  static async deleteSynchronization(id, options = {}) {
    try {
      const {
        confirmHardCascade = true,
        confirmMessage = "REMOVER TODOS OS DADOS DEPENDENTES",
      } = options;

      const headers = confirmHardCascade
        ? {
            "X-Confirm-HardCascade": "true",
            "X-Confirm-Message": confirmMessage,
          }
        : undefined;

      const response = await api.delete(`/sync/${id}`, { headers });
      return response.data;
    } catch (error) {
      console.error("Erro ao excluir sincronização:", error);
      throw error;
    }
  }

  // Upload de arquivo de orçamento para sincronização
  static async uploadOrcamentoFile(syncId, file) {
    try {
      if (!syncId) {
        throw new Error("ID da sincronização é obrigatório para upload");
      }

      const formData = new FormData();
      formData.append("files", file);
      formData.append("idSync", syncId);

      const response = await api.post("/sync/uploadFileOrcamento", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      console.error("Erro ao fazer upload do arquivo:", error);
      throw error;
    }
  }

  // Remover arquivo de orçamento
  static async removeOrcamentoFile(syncId) {
    try {
      const response = await api.post("/sync/removeFileOrcamento", {
        idSync: syncId,
      });
      return response.data;
    } catch (error) {
      console.error("Erro ao remover arquivo:", error);
      throw error;
    }
  }

  // Buscar arquivo
  static async getFile(fileName) {
    try {
      const response = await api.get(`/sync/getFile/${fileName}`, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar arquivo:", error);
      throw error;
    }
  }

  // Executar sincronização (simulado - implementar quando houver endpoint)
  static async runSynchronization(id) {
    try {
      // Por enquanto simular - implementar quando houver endpoint no backend
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return { success: true };
    } catch (error) {
      console.error("Erro ao executar sincronização:", error);
      throw error;
    }
  }

  // Pausar sincronização (simulado - implementar quando houver endpoint)
  static async pauseSynchronization(id) {
    try {
      // Por enquanto simular - implementar quando houver endpoint no backend
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return { success: true };
    } catch (error) {
      console.error("Erro ao pausar sincronização:", error);
      throw error;
    }
  }

  // Reativar sincronização (simulado - implementar quando houver endpoint)
  static async resumeSynchronization(id) {
    try {
      // Por enquanto simular - implementar quando houver endpoint no backend
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return { success: true };
    } catch (error) {
      console.error("Erro ao reativar sincronização:", error);
      throw error;
    }
  }

  // Testar conexão com ERP (simulado - implementar quando houver endpoint)
  static async testConnection(credentials) {
    try {
      // Por enquanto simular - implementar quando houver endpoint no backend
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return { success: true, message: "Conexão estabelecida com sucesso" };
    } catch (error) {
      console.error("Erro ao testar conexão:", error);
      throw error;
    }
  }

  static async getForecastRules(integrationId) {
    const response = await api.get(
      `/sync/integrations/${integrationId}/forecast-rules`,
    );
    return response.data?.response ?? [];
  }

  static async upsertForecastRule(
    integrationId,
    externalCanalVendaId,
    payload,
  ) {
    const response = await api.put(
      `/sync/integrations/${integrationId}/forecast-rules/${externalCanalVendaId}`,
      payload,
    );
    return response.data?.response ?? null;
  }

  static async deactivateForecastRule(integrationId, externalCanalVendaId) {
    await api.delete(
      `/sync/integrations/${integrationId}/forecast-rules/${externalCanalVendaId}`,
    );
  }

  // Buscar histórico de sincronizações (simulado - implementar quando houver endpoint)
  static async getSynchronizationHistory(id, limit = 10) {
    try {
      // Por enquanto simular - implementar quando houver endpoint no backend
      return [];
    } catch (error) {
      console.error("Erro ao buscar histórico:", error);
      throw error;
    }
  }

  // Buscar estatísticas de sincronização (simulado - implementar quando houver endpoint)
  static async getSynchronizationStats(id) {
    try {
      // Por enquanto simular - implementar quando houver endpoint no backend
      return {
        totalRecords: 0,
        lastSync: null,
        successfulSyncs: 0,
        failedSyncs: 0,
      };
    } catch (error) {
      console.error("Erro ao buscar estatísticas:", error);
      throw error;
    }
  }

  // Buscar ERPs disponíveis
  static async getAvailableERPsSelect() {
    try {
      // Retornar ERPs disponíveis conforme enum do backend
      return [
        { id: 0, value: 0, name: "Omie", label: "Omie" },
        { id: 2, value: 2, name: "Nibo", label: "Nibo" },
        { id: 3, value: 3, name: "Excel", label: "Excel" },
        { id: 5, value: 5, name: "Conta Azul", label: "Conta Azul" },
        { id: 6, value: 6, name: "Bling", label: "Bling" },
        { id: 7, value: 7, name: "Vehro", label: "Vehro" },
      ];
    } catch (error) {
      console.error("Erro ao buscar ERPs:", error);
      throw error;
    }
  }

  static async getAvailableERPs() {
    try {
      // Retornar ERPs disponíveis conforme enum do backend
      return [
        { id: 0, value: 0, name: "Omie", label: "Omie" },
        { id: 2, value: 2, name: "Nibo", label: "Nibo" },
        { id: 3, value: 3, name: "Excel", label: "Excel" },
        { id: 5, value: 5, name: "Conta Azul", label: "Conta Azul" },
        { id: 6, value: 6, name: "Bling", label: "Bling" },
        { id: 7, value: 7, name: "Vehro", label: "Vehro" },
      ];
    } catch (error) {
      console.error("Erro ao buscar ERPs:", error);
      throw error;
    }
  }
  // Validar dados de sincronização baseado no tipo de ERP
  static validateSynchronizationData(data) {
    const errors = [];

    if (!data.nome || data.nome.trim() === "") {
      errors.push("Nome da sincronização é obrigatório");
    }

    if (!data.projectId) {
      errors.push("Projeto é obrigatório");
    }

    if (data.erp === undefined || data.erp === null) {
      errors.push("ERP é obrigatório");
    }

    // Validações específicas por tipo de ERP
    switch (data.erp) {
      case 0: // Omie
        if (!data.appKey || data.appKey.trim() === "") {
          errors.push("APP Key é obrigatório para Omie");
        }
        if (!data.appSecret || data.appSecret.trim() === "") {
          errors.push("APP Secret é obrigatório para Omie");
        }
        break;

      case 1: // ContaAzul
      case 2: // Nibo
        if (!data.login || data.login.trim() === "") {
          errors.push("Login é obrigatório");
        }
        if (!data.senha || data.senha.trim() === "") {
          errors.push("Senha é obrigatória");
        }
        break;

      case 3: // Excel
        // Apenas nome é obrigatório (já validado acima)
        break;

      case 5: // ContaAzulV2
      case 6: // Bling — credenciais via OAuth; nenhum campo adicional obrigatório no formulário
        break;

      case 7: // Vehro — token da API validado no formulário (obrigatório só na criação)
        break;

      default:
        errors.push("Tipo de ERP inválido");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // Verificar se já existe sincronização com o mesmo ERP para o projeto
  static async checkDuplicateERP(projectId, erpId, currentSyncId = null) {
    try {
      // Buscar todas as sincronizações e filtrar pelo projeto atual
      const allSyncs = await this.getSynchronizations({
        page: 1,
        pageSize: 1000, // Usar um número alto para pegar todas
      });

      // Filtrar sincronizações do projeto atual com o mesmo ERP
      const duplicateSyncs = allSyncs.filter((sync) => {
        const isCurrentProject =
          sync.projectId === projectId || sync.project?.id === projectId;
        const isSameERP = sync.erp === erpId;
        const isNotCurrentSync = currentSyncId
          ? sync.id !== currentSyncId
          : true;

        return isCurrentProject && isSameERP && isNotCurrentSync;
      });

      return {
        hasDuplicate: duplicateSyncs.length > 0,
        existingSync: duplicateSyncs[0] || null,
      };
    } catch (error) {
      console.error("❌ Erro ao verificar ERP duplicado:", error);
      // Em caso de erro na verificação, permitir continuar mas loggar o erro
      return { hasDuplicate: false, existingSync: null };
    }
  }

  // Buscar arquivos de orçamento de uma sincronização
  static async getBudgetFiles(syncId) {
    try {
      const response = await api.get(`/syncBudget/getBySyncId/${syncId}`);
      return response.data?.response || [];
    } catch (error) {
      console.error("Erro ao buscar arquivos de orçamento:", error);
      throw error;
    }
  }

  // Buscar arquivo específico de orçamento
  static async getBudgetFile(syncId, file, syncBudgetId = null) {
    try {
      const requestData = {
        syncId: syncId,
        fileUrl: file,
        syncBudgetId: syncBudgetId,
      };

      const response = await api.post(`/sync/getBudgetFile`, requestData);
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar arquivo de orçamento:", error);
      throw error;
    }
  }

  // Criar ou atualizar arquivo de orçamento
  static async createBudgetFile(syncId, file, year, id = null) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("syncId", syncId);
      formData.append("year", year);
      if (id) {
        formData.append("id", id);
      }

      const response = await api.post("/syncBudget/upsert", formData);
      return response.data;
    } catch (error) {
      console.error("Erro ao criar arquivo de orçamento:", error);
      throw error;
    }
  }

  // Deletar arquivo de orçamento
  static async deleteBudgetFile(syncBudgetId) {
    try {
      const response = await api.delete(`/syncBudget/delete/${syncBudgetId}`);
      return response.data;
    } catch (error) {
      console.error("Erro ao deletar arquivo de orçamento:", error);
      throw error;
    }
  }

  // Download de arquivo de orçamento
  static async downloadBudgetFile(syncBudgetId, fileUrl) {
    try {
      const response = await api.get(
        `/syncBudget/downloadFile/${syncBudgetId}`,
        {
          responseType: "blob",
        },
      );

      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${fileUrl}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error("Erro ao baixar arquivo de orçamento:", error);
      throw error;
    }
  }

  // Transformar dados do frontend para o formato do backend (SyncDto)
  static transformDataForBackend(formData) {
    const syncDto = {
      id: formData.id || null,
      projectId: formData.projectId,
      type: formData.type || 1,
      registrationCount: formData.registrationCount || 0,
      erp: formData.erp,
      name: formData.name,
      nomeIntegracao: formData.nomeIntegracao,
      appKey: formData.appKey || null,
      externalId: formData.ExternalId || null,
      appSecret: formData.appSecret || null,
      login: formData.login || null,
      senha: formData.senha || null,
      isConfigured: formData.type === 2 ? true : formData.isConfigured || false,

      Credentials: {
        NomeIntegracao: formData.nomeIntegracao,
        AppKey: formData.erp === 0 ? formData.appKey : null,
        AppSecret: formData.erp === 0 ? formData.appSecret : null,
        Login: formData.erp === 1 || formData.erp === 2 ? formData.login : null,
        Senha: formData.erp === 1 || formData.erp === 2 ? formData.senha : null,
        AgrupadorSyncs:
          formData.type === 3 ? formData.credentials.agrupadorSyncs : null,
        Owner: formData.credentials.owner || null,
        Empresa: formData.credentials.empresa || null,
        Arquivos: formData.credentials.arquivos || null,
        AuthToken: formData.erp === 1 ? formData.credentials.authToken : null,
      },
      fileOrcamento: formData.fileOrcamento || null,
      status: formData.status || 1,
      lastSync: formData.lastSync || new Date().toISOString(),
      dfcConfig: formData.dfcConfig || [],
      financialGroupConfig: formData.financialGroupConfig || [],
      kpiConfig: formData.kpiConfig || [],
      dreConfig: formData.dreConfig || [],
      budgetConfig: formData.budgetConfig || [],
      useOmieExibeFluxoCategories: false,
    };

    return syncDto;
  }

  // Transformar dados do backend para o formato do frontend
  static transformDataFromBackend(syncDto) {
    return {
      id: syncDto.id || syncDto.Id,
      projectId: syncDto.projectId || syncDto.ProjectId,
      projectName:
        (syncDto.project && syncDto.project.name) ||
        syncDto.projectName ||
        syncDto.ProjectName ||
        null,
      project: syncDto.project || syncDto.Project || null,
      erp: syncDto.erp ?? syncDto.Erp ?? null,
      name: syncDto.name || syncDto.Name,
      lastSync: syncDto.lastSync || syncDto.LastSync,
      created: syncDto.created || syncDto.Created,
      updated: syncDto.updated || syncDto.Updated,
      createdBy: syncDto.createdBy || syncDto.CreatedBy,
      updatedBy: syncDto.updatedBy || syncDto.UpdatedBy,
      externalId: syncDto.ExternalId || syncDto.externalId || null,
      integrationId:
        syncDto.integrationId ||
        syncDto.IntegrationId ||
        syncDto.ExternalId ||
        syncDto.externalId ||
        syncDto.id ||
        syncDto.Id ||
        null,
      lastRequestIncAt:
        syncDto.lastRequestIncAt || syncDto.LastRequestIncAt || null,
      userRequestInc: syncDto.userRequestInc || syncDto.UserRequestInc || null,
      lastStatus:
        syncDto.lastStatus ?? syncDto.LastStatus ?? syncDto.laststatus ?? null,
      lastStatusMessage:
        syncDto.lastStatusMessage ??
        syncDto.LastStatusMessage ??
        syncDto.laststatusmessage ??
        null,
      status: syncDto.status,
      registrationCount: syncDto.registrationCount,
      type: syncDto.type || syncDto.Type || 1,
      useErpDreConfig:
        syncDto.useErpDreConfig ?? syncDto.UseErpDreConfig ?? false,
      useOmieExibeFluxoCategories:
        syncDto.useOmieExibeFluxoCategories ??
        syncDto.UseOmieExibeFluxoCategories ??
        syncDto.useOmieFlowCategories ??
        syncDto.UseOmieFlowCategories ??
        syncDto.useExibeFluxoCategories ??
        syncDto.UseExibeFluxoCategories ??
        false,
      fileOrcamento: syncDto.fileOrcamento,
      isConfigured: syncDto.isConfigured || syncDto.IsConfigured || false,
      // Fechamentos: indicadores da lista de empresas (o texto do contexto é carregado sob demanda).
      hasClosingContext:
        syncDto.hasClosingContext ?? syncDto.HasClosingContext ?? false,
      hasFechamentosModule:
        syncDto.hasFechamentosModule ?? syncDto.HasFechamentosModule ?? false,
      // Extrai credenciais
      credentials: syncDto.credentials || syncDto.Credentials || {},
      // Parâmetros globais
      dfcConfig: syncDto.dfcConfig || syncDto.dfcConfig || [],
      financialGroupConfig:
        syncDto.financialGroupConfig || syncDto.financialGroupConfig || [],
      kpiConfig: syncDto.kpiConfig || syncDto.kpiConfig || [],
      dreConfig: syncDto.dreConfig || syncDto.dreConfig || [],
      budgetConfig: syncDto.budgetConfig || syncDto.budgetConfig || [],
    };
  }

  static async getSynchronizationDreConfig(id) {
    try {
      const { client, url } = resolveViewRoute(
        `/sync/getDreConfig/${id}`,
        `/public/view/sync/${id}/dre-config`,
      );
      const response = await client.get(url);
      // Pode vir como { response: {...} } ou como objeto direto; projetos/departamentos podem ser nulos
      const raw = response?.data?.response ?? response?.data ?? {};
      // Garante objeto e evita quebrar quando chaves novas vierem nulas/ausentes
      if (raw && typeof raw === "object") return raw;
      return {};
    } catch (error) {
      console.error("Erro ao buscar sincronização:", error);
      throw error;
    }
  }

  static async getDREConfig(syncId) {
    return this.getSynchronizationDreConfig(syncId);
  }

  static async getSynchronizationCategoryList(id) {
    try {
      const response = await api.get(`/sync/${id}/getCategoryList`);
      return response?.data?.response ?? [];
    } catch (error) {
      console.error("Erro ao buscar categoryList da sincronização:", error);
      throw error;
    }
  }

  static async getSynchronizationDreConfigTotalizadores(id) {
    try {
      const response = await api.get(`/sync/getDreConfigTotalizador/${id}`);
      return response.data?.response || [];
    } catch (error) {
      console.error("Erro ao buscar sincronização:", error);
      throw error;
    }
  }
  ///sync/getSyncKPIDRE/{syncId}

  static async saveSynchronizationDreConfig(
    dreConfig,
    dreCatConfig,
    syncId,
    tipoDrePag = null,
  ) {
    try {
      const payload = {
        SyncDre: dreConfig,
        SyncDreCat: dreCatConfig,
        SyncId: syncId,
      };
      // Regra DRE por pagamento (Omie): só envia quando definida, para não sobrescrever no backend.
      if (tipoDrePag !== null && tipoDrePag !== undefined) {
        payload.TipoDrePag = tipoDrePag;
      }
      const response = await api.post(`/sync/saveDreConfig`, payload);
      return response.data?.response || [];
    } catch (error) {
      console.error("Erro ao buscar sincronização:", error);
      throw error;
    }
  }

  static async setDreTotalizadorAV(syncId, syncDreId) {
    try {
      const response = await api.post("/sync/setDreTotalizadorAV", {
        syncId,
        syncDreId: syncDreId ?? null,
      });
      return response.data?.response ?? response.data;
    } catch (error) {
      console.error("Erro ao salvar Base A.V. do DRE:", error);
      throw error;
    }
  }

  // --- Parâmetros individuais: carregar ---
  static async getDFCConfig(syncId) {
    try {
      const { client, url } = resolveViewRoute(
        `/sync/getDFCConfig/${syncId}`,
        `/public/view/sync/${syncId}/dfc-config`,
      );
      const response = await client.get(url);
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar configuração DFC:", error);
      throw error;
    }
  }

  static async getFinancialGroupConfig(syncId) {
    try {
      const response = await api.get(`/sync/getFinancialGroupConfig/${syncId}`);
      return response.data;
    } catch (error) {
      console.error(
        "Erro ao buscar configuração de Grupos Financeiros:",
        error,
      );
      throw error;
    }
  }

  static async getKPIConfig(syncId) {
    try {
      const response = await api.get(`/sync/getKPIConfig/${syncId}`);
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar configuração de KPIs:", error);
      throw error;
    }
  }

  // --- Orçamentos ---
  static async getBudgetCategories(syncId) {
    try {
      const { client, url } = resolveViewRoute(
        `/orcamentos/${syncId}/categorias`,
        `/public/view/orcamentos/${syncId}/categorias`,
      );
      const response = await client.get(url);
      return response.data?.response ?? response.data ?? [];
    } catch (error) {
      console.error("Erro ao buscar categorias de orçamento:", error);
      throw error;
    }
  }

  static async getBudget(syncId, ano) {
    try {
      const { client, url } = resolveViewRoute(
        `/orcamentos/${syncId}`,
        `/public/view/orcamentos/${syncId}`,
      );
      const response = await client.get(url, { params: { ano } });
      return response.data?.response ?? response.data ?? [];
    } catch (error) {
      console.error("Erro ao buscar orçamento:", error);
      throw error;
    }
  }

  static async fillBudgetWithRealized(syncId, ano) {
    try {
      const response = await api.get(`/orcamentos/${syncId}/preencher`, {
        params: { ano },
      });
      return response.data?.response ?? response.data ?? {};
    } catch (error) {
      console.error("Erro ao preencher orçamento com realizados:", error);
      throw error;
    }
  }

  // Somente Omie: inicia em segundo plano a importação do ListarOrcamentos via integrador.
  // Devolve o status inicial ({ jobId, status, percentual, ... }); progresso e resultado chegam pelo SSE
  // /orcamentos/importacoes-omie/{jobId}/stream (ver OmieBudgetImportContext).
  static async startOmieBudgetImport(syncId, ano) {
    try {
      const response = await api.post(`/orcamentos/${syncId}/importar-omie`, null, {
        params: { ano },
        skipNotificationToast: true,
      });
      return response.data?.response ?? response.data ?? {};
    } catch (error) {
      console.error("Erro ao iniciar importação de orçamento da Omie:", error);
      throw error;
    }
  }

  static async getActiveOmieBudgetImport(syncId) {
    try {
      const response = await api.get(`/orcamentos/${syncId}/importar-omie/em-andamento`, {
        skipNotificationToast: true,
      });
      return response.data?.response ?? null;
    } catch (error) {
      console.error("Erro ao consultar importação de orçamento da Omie em andamento:", error);
      throw error;
    }
  }

  static async saveBudget(payload) {
    try {
      const response = await api.post("/orcamentos", payload);
      return response.data;
    } catch (error) {
      console.error("Erro ao salvar orçamento:", error);
      throw error;
    }
  }

  // --- Parâmetros individuais: salvar ---
  static async saveDFCConfig(dfcConfigList) {
    try {
      const response = await api.post("/sync/saveDFCConfig", dfcConfigList);
      return response.data;
    } catch (error) {
      console.error("Erro ao salvar configuração DFC:", error);
      throw error;
    }
  }

  static async getAllBackoffice(filters = {}) {
    try {
      const response = await api.post("/syncBackoffice/getAll", filters);
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar sincronizações:", error);
      throw error;
    }
  }

  static async saveFinancialGroupConfig(financialGroupConfigList) {
    try {
      const response = await api.post(
        "/sync/saveFinancialGroupConfig",
        financialGroupConfigList,
      );
      return response.data;
    } catch (error) {
      console.error(
        "Erro ao salvar configuração de Grupos Financeiros:",
        error,
      );
      throw error;
    }
  }

  static async saveKPIConfig(kpiConfigList) {
    try {
      const response = await api.post("/sync/saveKPIConfig", kpiConfigList);
      return response.data;
    } catch (error) {
      console.error("Erro ao salvar configuração de KPIs:", error);
      throw error;
    }
  }

  static async migrateToCAV2(id) {
    try {
      const response = await api.get(`/sync/migrateCA/${id}`);
      return response.data?.response || {};
    } catch (error) {
      console.error("Erro ao buscar sincronização:", error);
      throw error;
    }
  }

  static async getFullCharge(erp, externalId) {
    try {
      const response = await api.post("/sync/getFullCharge", {
        erp: erp,
        integrationId: externalId,
      });
      return response.data?.response || {};
    } catch (error) {
      console.error("Erro ao executar Full Charge:", error);
      throw error;
    }
  }

  static async updateSyncDRE(erp, externalId, recharge) {
    try {
      const response = await api.post("/sync/updateSyncDRE", {
        erp: erp,
        integration_id: externalId,
        recharge,
      });
      return response.data?.response || {};
    } catch (error) {
      console.error("Erro ao atualizar Sync DRE:", error);
      throw error;
    }
  }

  static async setExcelDreConfig(syncId) {
    try {
      const response = await api.post(`/sync/setExcelDreConfig/${syncId}`);
      return response.data?.response || response.data;
    } catch (error) {
      console.error(
        "Erro ao criar configuração inicial de DRE para Excel:",
        error,
      );
      throw error;
    }
  }

  static getCategoriasComLancamentoSemDre(syncId) {
    return api.get(`/sync/${syncId}/categorias-com-lancamento-sem-dre`)
      .then((res) => res.data?.response ?? res.data);
  }
}

export default SynchronizationService;
